import QRCode from 'qrcode';

export interface LabelTemplate {
  id: string;
  name: string;
  width: number; // mm
  height: number; // mm
  description: string;
  supportsLogo: boolean;
  supportsQR: boolean;
  supportsText: boolean;
}

export interface LabelData {
  itemName: string;
  sku?: string;
  serialNumber?: string;
  qrCode?: string;
  customText?: string;
  template: LabelTemplate;
  options: {
    includeQR: boolean;
    includeLogo: boolean;
  };
}

export interface LabelFileResult {
  success: boolean;
  message: string;
  fileName?: string;
  fileContent?: string | ArrayBuffer;
}

class LabelFileService {
  /**
   * Generiert eine Brother P-Touch Label-Datei (.lbl)
   */
  async generateBrotherLabelFile(labelData: LabelData): Promise<LabelFileResult> {
    try {
      const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;

      // Brother P-Touch Label-Datei Format
      let labelContent = '';

      // Header
      labelContent += `[Label]\n`;
      labelContent += `Width=${template.width}\n`;
      labelContent += `Height=${template.height}\n`;
      labelContent += `Density=2\n`;
      labelContent += `Speed=2\n`;
      labelContent += `Direction=0\n`;
      labelContent += `Margin=0\n\n`;

      // Content
      labelContent += `[Content]\n`;

      let yPosition = 0;
      const lineHeight = 8; // mm

      // Logo (falls gewünscht)
      if (options.includeLogo && template.supportsLogo) {
        labelContent += `Text=Westfalen AG\n`;
        labelContent += `X=2\n`;
        labelContent += `Y=${yPosition}\n`;
        labelContent += `Font=0\n`;
        labelContent += `Size=6\n\n`;
        yPosition += lineHeight;
      }

      // Item Name
      labelContent += `Text=${itemName}\n`;
      labelContent += `X=2\n`;
      labelContent += `Y=${yPosition}\n`;
      labelContent += `Font=1\n`;
      labelContent += `Size=8\n\n`;
      yPosition += lineHeight;

      // SKU (falls vorhanden)
      if (sku) {
        labelContent += `Text=Mat. Nr: ${sku}\n`;
        labelContent += `X=2\n`;
        labelContent += `Y=${yPosition}\n`;
        labelContent += `Font=0\n`;
        labelContent += `Size=6\n\n`;
        yPosition += lineHeight;
      }

      // Serial Number (falls vorhanden)
      if (serialNumber) {
        labelContent += `Text=SN: ${serialNumber}\n`;
        labelContent += `X=2\n`;
        labelContent += `Y=${yPosition}\n`;
        labelContent += `Font=0\n`;
        labelContent += `Size=6\n\n`;
        yPosition += lineHeight;
      }

      // QR Code (falls gewünscht)
      if (options.includeQR && template.supportsQR && qrCode) {
        labelContent += `QRCode=${qrCode}\n`;
        labelContent += `X=${Math.max(2, (template.width - 20) / 2)}\n`;
        labelContent += `Y=${yPosition}\n`;
        labelContent += `Size=8\n`;
        labelContent += `ErrorCorrection=1\n\n`;
        yPosition += 20; // QR Code Höhe
      }

      // Custom Text (falls vorhanden)
      if (customText) {
        labelContent += `Text=${customText}\n`;
        labelContent += `X=2\n`;
        labelContent += `Y=${yPosition}\n`;
        labelContent += `Font=0\n`;
        labelContent += `Size=6\n\n`;
      }

      const fileName = `label-${itemName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.lbl`;

      return {
        success: true,
        message: 'Label-Datei erfolgreich generiert',
        fileName,
        fileContent: labelContent
      };
    } catch (error) {
      console.error('Fehler beim Generieren der Label-Datei:', error);
      return {
        success: false,
        message: `Fehler beim Generieren der Label-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      };
    }
  }

  /**
   * Generiert eine Brother P-Touch Editor 6 Label-Datei (.lbx)
   */
  async generateBrotherLbxFile(labelData: LabelData): Promise<LabelFileResult> {
    try {
      const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;

      // .lbx ist ein ZIP-Archiv mit XML-Dateien
      // Konvertiere mm zu pt (1mm = 2.834645669pt)
      const mmToPt = 2.834645669;
      const widthPt = template.width * mmToPt;
      const heightPt = template.height * mmToPt;

      // Erstelle label.xml (Hauptlayout)
      const labelXml = await this.createLabelXml(labelData, widthPt, heightPt, mmToPt);
      
      // Erstelle prop.xml (Metadaten)
      const propXml = this.createPropXml();

      // Erstelle ZIP-Archiv mit JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      zip.file('label.xml', labelXml);
      zip.file('prop.xml', propXml);

      // Generiere und füge BMP-Dateien hinzu
      if (options.includeLogo) {
        // Berechne Logo-Größe basierend auf Label-Breite
        const logoSize = heightPt * 0.8; // 80% der Label-Höhe
        const logoSizePx = Math.round(logoSize * 3.7795275591); // Konvertiere pt zu px (96 DPI)
        console.log(`Logo-Größe für ${template.height}mm Label: ${logoSize}pt = ${logoSizePx}px`);
        const logoBmp = await this.generateLogoBmp(logoSizePx);
        zip.file('Object0.bmp', logoBmp);
      }

      if (options.includeQR && qrCode) {
        // Berechne QR-Code-Größe basierend auf Label-Breite
        const qrCodeSize = heightPt * 0.8; // 80% der Label-Höhe
        const qrCodeSizePx = Math.round(qrCodeSize * 3.7795275591); // Konvertiere pt zu px (96 DPI)
        console.log(`QR-Code-Größe für ${template.height}mm Label: ${qrCodeSize}pt = ${qrCodeSizePx}px`);
        const qrBmp = await this.generateQrCodeBmp(qrCode, qrCodeSizePx);
        zip.file('Object1.bmp', qrBmp);
      }

      // Generiere ZIP als Blob
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // Konvertiere Blob zu ArrayBuffer für Download
      const arrayBuffer = await zipBlob.arrayBuffer();
      const fileName = `label-${itemName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.lbx`;

      return {
        success: true,
        message: 'Brother P-Touch Editor 6 Datei (.lbx) erfolgreich generiert',
        fileName,
        fileContent: arrayBuffer // Gebe ArrayBuffer zurück statt Base64
      };
    } catch (error) {
      console.error('Fehler beim Generieren der .lbx-Datei:', error);
      return {
        success: false,
        message: `Fehler beim Generieren der .lbx-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      };
    }
  }

  /**
   * Erstellt die label.xml für Brother P-Touch Editor (dynamisch an Label-Breite angepasst)
   */
  private async createLabelXml(labelData: LabelData, widthPt: number, heightPt: number, mmToPt: number): Promise<string> {
    const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;
    
    // Berechne die tatsächlichen Dimensionen basierend auf dem Template
    const paperWidth = heightPt; // Bandbreite (template.height in pt)
    const paperHeight = widthPt * 2; // Kürzere Dimension für autoLength
    const backgroundWidth = heightPt - 5.6; // Abzüglich Margins
    const backgroundHeight = Math.min(paperHeight / 2, widthPt); // Begrenzte Höhe
    
    // Dynamische Berechnung der Element-Größen basierend auf Label-Breite
    const labelHeightMm = template.height; // mm
    const labelHeightPt = heightPt; // pt
    
    // Berechne Element-Größen proportional zur Label-Breite
    // 80% der Label-Höhe für sichere Darstellung
    const logoSize = labelHeightPt * 0.8; // Logo nimmt 80% der Label-Höhe ein
    const qrCodeSize = labelHeightPt * 0.8; // QR-Code gleiche Größe wie Logo
    const textAreaWidth = labelHeightPt * 3; // Text-Bereich nimmt 3x die Label-Höhe ein
    const textAreaHeight = labelHeightPt * 0.9; // Text-Bereich nimmt 90% der Label-Höhe ein
    
    // Positionen basierend auf Label-Breite
    const logoX = 2.8; // Links
    const logoY = 2.2 + (labelHeightPt - logoSize) / 2; // Vertikal zentriert
    const qrCodeX = logoX + logoSize + 2; // Rechts vom Logo
    const qrCodeY = 2.2 + (labelHeightPt - qrCodeSize) / 2; // Vertikal zentriert
    const textX = qrCodeX + qrCodeSize + 2; // Rechts vom QR-Code
    const textY = 2.2;
    
    // Schriftgrößen basierend auf Label-Breite
    const titleSize = Math.max(8, Math.min(16, labelHeightMm * 0.6)); // 60% der Label-Höhe
    const subtitleSize = Math.max(6, Math.min(12, labelHeightMm * 0.4)); // 40% der Label-Höhe
    const smallTextSize = Math.max(5, Math.min(10, labelHeightMm * 0.3)); // 30% der Label-Höhe
    
    console.log('Dynamisches Layout für', labelHeightMm + 'mm Label:', {
      logoSize: logoSize + 'pt',
      qrCodeSize: qrCodeSize + 'pt',
      textAreaWidth: textAreaWidth + 'pt',
      titleSize: titleSize + 'pt',
      subtitleSize: subtitleSize + 'pt'
    });
    
    let xml = '<?xml version="1.0" encoding="UTF-8"?>';
    xml += '<pt:document xmlns:pt="http://schemas.brother.info/ptouch/2007/lbx/main" ';
    xml += 'xmlns:style="http://schemas.brother.info/ptouch/2007/lbx/style" ';
    xml += 'xmlns:text="http://schemas.brother.info/ptouch/2007/lbx/text" ';
    xml += 'xmlns:draw="http://schemas.brother.info/ptouch/2007/lbx/draw" ';
    xml += 'xmlns:image="http://schemas.brother.info/ptouch/2007/lbx/image" ';
    xml += 'xmlns:barcode="http://schemas.brother.info/ptouch/2007/lbx/barcode" ';
    xml += 'xmlns:database="http://schemas.brother.info/ptouch/2007/lbx/database" ';
    xml += 'xmlns:table="http://schemas.brother.info/ptouch/2007/lbx/table" ';
    xml += 'xmlns:cable="http://schemas.brother.info/ptouch/2007/lbx/cable" ';
    xml += 'version="1.10" generator="P-touch Editor 6.8.1.0 Windows">';
    
    xml += '<pt:body currentSheet="Blatt 1" direction="LTR">';
    xml += '<style:sheet name="Blatt 1">';
    
    // Paper-Einstellungen (dynamisch basierend auf Template)
    xml += `<style:paper media="0" width="${paperWidth.toFixed(1)}pt" height="${paperHeight.toFixed(1)}pt" `;
    xml += 'marginLeft="2.2pt" marginTop="2.8pt" marginRight="2.2pt" marginBottom="2.8pt" ';
    xml += 'orientation="landscape" autoLength="true" monochromeDisplay="true" ';
    xml += 'printColorDisplay="false" printColorsID="0" paperColor="#FFFFFF" ';
    xml += 'paperInk="#000000" split="1" format="260" backgroundTheme="0" ';
    xml += 'printerID="28720" printerName="Brother PT-P950NW">';
    xml += '</style:paper>';
    
    xml += '<style:cutLine regularCut="0pt" freeCut=""></style:cutLine>';
    
    // Background (dynamisch)
    xml += `<style:backGround x="2.8pt" y="2.2pt" width="${backgroundWidth.toFixed(1)}pt" height="${backgroundHeight.toFixed(1)}pt" `;
    xml += 'brushStyle="NULL" brushId="0" userPattern="NONE" userPatternId="0" ';
    xml += 'color="#000000" printColorNumber="1" backColor="#FFFFFF" ';
    xml += 'backPrintColorNumber="0">';
    xml += '</style:backGround>';
    
    xml += '<pt:objects>';

    // 1. Logo-Bild (links)
    if (options.includeLogo) {
      xml += this.createImageObject(0, logoX, logoY, logoSize, logoSize, 'wag_logo_bw.png', 'Object0.bmp');
    }

    // 2. QR-Code-Bild (rechts vom Logo)
    if (options.includeQR && qrCode) {
      xml += this.createImageObject(1, qrCodeX, qrCodeY, qrCodeSize, qrCodeSize, `qr-code-${itemName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.png`, 'Object1.bmp');
    }

    // 3. Haupttitel (Text1) - fett, größer, auf gleicher Höhe wie Bilder
    xml += this.createTextObjectWithStyle(
      0, // ID
      textX, // x
      logoY, // y (gleiche Höhe wie Logo/QR-Code)
      textAreaWidth, // width
      logoSize * 0.3, // height (30% der Bildhöhe)
      itemName,
      'Helsinki',
      titleSize, // size (dynamisch)
      700, // weight (bold)
      false, // italic
      false // underline
    );

    // 4. Materialnummer (Text2) - normal, mittig zwischen Bildern
    if (sku) {
      xml += this.createTextObjectWithStyle(
        1, // ID
        textX, // x
        logoY + logoSize * 0.3, // y (mittig zwischen Bildern)
        textAreaWidth, // width
        logoSize * 0.2, // height (20% der Bildhöhe)
        `Mat. Nr: ${sku}`,
        'Helsinki',
        subtitleSize, // size (dynamisch)
        400, // weight (normal)
        false, // italic
        false // underline
      );
    }

    // 5. Standort-Information (Text3) - normal, mittig zwischen Bildern
    const locationText = customText || 'Standort: LZA Hörstel (0283) -> Geeignet für 0033, 0283';
    xml += this.createTextObjectWithStyle(
      2, // ID
      textX, // x
      logoY + logoSize * 0.5, // y (mittig zwischen Bildern)
      textAreaWidth, // width
      logoSize * 0.2, // height (20% der Bildhöhe)
      locationText,
      'Helsinki',
      smallTextSize, // size (dynamisch)
      400, // weight (normal)
      false, // italic
      false // underline
    );

    // 6. Firmenname (Text4) - fett, auf gleicher Höhe wie untere Bildkante
    xml += this.createTextObjectWithStyle(
      3, // ID
      textX, // x
      logoY + logoSize * 0.7, // y (unten, auf Höhe der unteren Bildkante)
      textAreaWidth, // width
      logoSize * 0.2, // height (20% der Bildhöhe)
      'Eigentum der Westfalen AG',
      'Helsinki',
      smallTextSize, // size (dynamisch)
      700, // weight (bold)
      false, // italic
      false // underline
    );

    xml += '</pt:objects>';
    xml += '</style:sheet>';
    xml += '</pt:body>';
    xml += '</pt:document>';

    return xml;
  }

  /**
   * Erstellt ein Bild-Objekt für die label.xml (basierend auf originaler Struktur)
   */
  private createImageObject(id: number, x: number, y: number, width: number, height: number, originalName: string, fileName: string): string {
    let xml = '<image:image>';
    
    // Object Style (exakt wie im Original)
    xml += `<pt:objectStyle x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt" `;
    xml += 'backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" ';
    xml += 'angle="0" anchor="TOPLEFT" flip="NONE">';
    xml += '<pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"></pt:pen>';
    xml += '<pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"></pt:brush>';
    xml += `<pt:expanded objectName="Image${id + 1}" ID="0" lock="2" `;
    xml += 'templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" ';
    xml += 'allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"></pt:expanded>';
    xml += '</pt:objectStyle>';
    
    // Image Style (exakt wie im Original)
    xml += `<image:imageStyle originalName="${originalName}" alignInText="NONE" firstMerge="true" IpName="" fileName="${fileName}">`;
    xml += '<image:transparent flag="false" color="#FFFFFF"></image:transparent>';
    xml += '<image:trimming flag="false" shape="RECTANGLE" trimOrgX="0pt" trimOrgY="0pt" ';
    xml += `trimOrgWidth="${width * 0.4}pt" trimOrgHeight="${height * 0.4}pt"></image:trimming>`;
    xml += `<image:orgPos x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt"></image:orgPos>`;
    xml += '<image:effect effect="NONE" brightness="50" contrast="50" photoIndex="4"></image:effect>';
    xml += '<image:mono operationKind="BINARY" reverse="0" ditherKind="MESH" threshold="128" gamma="100" ';
    xml += 'ditherEdge="0" rgbconvProportionRed="30" rgbconvProportionGreen="59" ';
    xml += 'rgbconvProportionBlue="11" rgbconvProportionReversed="0"></image:mono>';
    xml += '</image:imageStyle>';
    xml += '</image:image>';
    
    return xml;
  }

  /**
   * Erstellt ein Text-Objekt mit spezifischem Stil (basierend auf originaler Struktur)
   */
  private createTextObjectWithStyle(id: number, x: number, y: number, width: number, height: number, 
    text: string, fontName: string, fontSize: number, fontWeight: number, 
    italic: boolean, underline: boolean): string {
    
    const escapedText = this.escapeXml(text);
    const textLength = text.length;
    
    let xml = '<text:text>';
    
    // Object Style (exakt wie im Original)
    xml += `<pt:objectStyle x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt" `;
    xml += 'backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" ';
    xml += 'angle="0" anchor="TOPLEFT" flip="NONE">';
    xml += '<pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"></pt:pen>';
    xml += '<pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"></pt:brush>';
    xml += `<pt:expanded objectName="Text${id + 1}" ID="0" lock="0" `;
    xml += 'templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" ';
    xml += 'allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"></pt:expanded>';
    xml += '</pt:objectStyle>';
    
    // Font Info (exakt wie im Original)
    xml += '<text:ptFontInfo>';
    xml += `<text:logFont name="${fontName}" width="0" italic="${italic}" weight="${fontWeight}" charSet="0" pitchAndFamily="34"></text:logFont>`;
    xml += `<text:fontExt effect="NOEFFECT" underline="${underline ? '1' : '0'}" strikeout="0" `;
    xml += `size="${fontSize}pt" orgSize="${fontSize * 2.7}pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>`;
    xml += '</text:ptFontInfo>';
    
    // Text Control (exakt wie im Original)
    xml += '<text:textControl control="LONGTEXTFIXED" clipFrame="false" aspectNormal="true" ';
    xml += 'shrink="true" autoLF="true" avoidImage="false"></text:textControl>';
    xml += '<text:textAlign horizontalAlignment="LEFT" verticalAlignment="TOP" inLineAlignment="BASELINE"></text:textAlign>';
    xml += '<text:textStyle vertical="false" nullBlock="false" charSpace="0" lineSpace="0" ';
    xml += `orgPoint="${fontSize * 1.1}pt" combinedChars="false"></text:textStyle>`;
    xml += '<text:transferSettings editOnPrintFormat="" editOnPrintOrder="0"></text:transferSettings>';
    
    // Text Data
    xml += `<pt:data>${escapedText}</pt:data>`;
    
    // String Items (ein Item für den gesamten Text)
    xml += `<text:stringItem charLen="${textLength}">`;
    xml += '<text:ptFontInfo>';
    xml += `<text:logFont name="${fontName}" width="0" italic="${italic}" weight="${fontWeight}" charSet="0" pitchAndFamily="34"></text:logFont>`;
    xml += `<text:fontExt effect="NOEFFECT" underline="${underline ? '1' : '0'}" strikeout="0" `;
    xml += `size="${fontSize}pt" orgSize="${fontSize * 2.7}pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>`;
    xml += '</text:ptFontInfo>';
    xml += '</text:stringItem>';
    
    xml += '</text:text>';
    
    return xml;
  }

  /**
   * Erstellt ein Text-Objekt basierend auf der originalen Brother P-Touch Editor Struktur
   */
  private createOriginalTextObject(id: number, x: number, y: number, width: number, height: number, text: string): string {
    const escapedText = this.escapeXml(text);
    const textLength = text.length;
    
    let xml = '<text:text>';
    
    // Object Style (exakt wie im Original)
    xml += `<pt:objectStyle x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt" `;
    xml += 'backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" ';
    xml += 'angle="0" anchor="TOPLEFT" flip="NONE">';
    xml += '<pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"></pt:pen>';
    xml += '<pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"></pt:brush>';
    xml += `<pt:expanded objectName="Text${id + 1}" ID="${id}" lock="0" `;
    xml += 'templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" ';
    xml += 'allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"></pt:expanded>';
    xml += '</pt:objectStyle>';
    
    // Font Info (exakt wie im Original)
    xml += '<text:ptFontInfo>';
    xml += '<text:logFont name="Helsinki" width="0" italic="false" weight="400" charSet="0" pitchAndFamily="34"></text:logFont>';
    xml += '<text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="18pt" orgSize="28.8pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>';
    xml += '</text:ptFontInfo>';
    
    // Text Control (exakt wie im Original)
    xml += '<text:textControl control="AUTOLEN" clipFrame="false" aspectNormal="true" ';
    xml += 'shrink="true" autoLF="false" avoidImage="false"></text:textControl>';
    xml += '<text:textAlign horizontalAlignment="LEFT" verticalAlignment="TOP" inLineAlignment="BASELINE"></text:textAlign>';
    xml += '<text:textStyle vertical="false" nullBlock="false" charSpace="0" lineSpace="0" ';
    xml += 'orgPoint="36.2pt" combinedChars="false"></text:textStyle>';
    xml += '<text:transferSettings editOnPrintFormat="" editOnPrintOrder="0"></text:transferSettings>';
    
    // Text Data
    xml += `<pt:data>${escapedText}</pt:data>`;
    
    // String Items (ein Item für den gesamten Text)
    xml += `<text:stringItem charLen="${textLength}">`;
    xml += '<text:ptFontInfo>';
    xml += '<text:logFont name="Helsinki" width="0" italic="false" weight="400" charSet="0" pitchAndFamily="34"></text:logFont>';
    xml += '<text:fontExt effect="NOEFFECT" underline="0" strikeout="0" size="18pt" orgSize="28.8pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>';
    xml += '</text:ptFontInfo>';
    xml += '</text:stringItem>';
    
    xml += '</text:text>';
    
    return xml;
  }

  /**
   * Erstellt ein Text-Objekt für die label.xml (alte Methode)
   */
  private createTextObject(id: number, x: number, y: number, width: number, height: number, 
    text: string, fontName: string, fontSize: number, fontWeight: number, 
    italic: boolean, underline: boolean): string {
    
    let xml = '<text:text>\n';
    xml += `<pt:objectStyle x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt" `;
    xml += 'backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" ';
    xml += 'angle="0" anchor="TOPLEFT" flip="NONE">\n';
    xml += '<pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"></pt:pen>\n';
    xml += '<pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"></pt:brush>\n';
    xml += `<pt:expanded objectName="Text${id + 1}" ID="${id}" lock="0" `;
    xml += 'templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" ';
    xml += 'allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"></pt:expanded>\n';
    xml += '</pt:objectStyle>\n';
    
    xml += '<text:ptFontInfo>\n';
    xml += `<text:logFont name="${fontName}" width="0" italic="${italic}" weight="${fontWeight}" charSet="0" pitchAndFamily="34"></text:logFont>\n`;
    xml += `<text:fontExt effect="NOEFFECT" underline="${underline ? '1' : '0'}" strikeout="0" `;
    xml += `size="${fontSize}pt" orgSize="${fontSize * 1.6}pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>\n`;
    xml += '</text:ptFontInfo>\n';
    
    xml += '<text:textControl control="AUTOLEN" clipFrame="false" aspectNormal="true" ';
    xml += 'shrink="true" autoLF="false" avoidImage="false"></text:textControl>\n';
    xml += '<text:textAlign horizontalAlignment="LEFT" verticalAlignment="TOP" inLineAlignment="BASELINE"></text:textAlign>\n';
    xml += '<text:textStyle vertical="false" nullBlock="false" charSpace="0" lineSpace="0" ';
    xml += `orgPoint="${fontSize * 2}pt" combinedChars="false"></text:textStyle>\n`;
    xml += '<text:transferSettings editOnPrintFormat="" editOnPrintOrder="0"></text:transferSettings>\n';
    
    xml += `<pt:data>${this.escapeXml(text)}</pt:data>\n`;
    
    xml += `<text:stringItem charLen="${text.length}">\n`;
    xml += '<text:ptFontInfo>\n';
    xml += `<text:logFont name="${fontName}" width="0" italic="${italic}" weight="${fontWeight}" charSet="0" pitchAndFamily="34"></text:logFont>\n`;
    xml += `<text:fontExt effect="NOEFFECT" underline="${underline ? '1' : '0'}" strikeout="0" `;
    xml += `size="${fontSize}pt" orgSize="${fontSize * 1.6}pt" textColor="#000000" textPrintColorNumber="1"></text:fontExt>\n`;
    xml += '</text:ptFontInfo>\n';
    xml += '</text:stringItem>\n';
    
    xml += '</text:text>\n';
    
    return xml;
  }

  /**
   * Erstellt ein Barcode-Objekt für die label.xml
   */
  private createBarcodeObject(id: number, x: number, y: number, width: number, height: number, data: string): string {
    let xml = '<barcode:barcode>\n';
    xml += `<pt:objectStyle x="${x}pt" y="${y}pt" width="${width}pt" height="${height}pt" `;
    xml += 'backColor="#FFFFFF" backPrintColorNumber="0" ropMode="COPYPEN" ';
    xml += 'angle="0" anchor="TOPLEFT" flip="NONE">\n';
    xml += '<pt:pen style="NULL" widthX="0.5pt" widthY="0.5pt" color="#000000" printColorNumber="1"></pt:pen>\n';
    xml += '<pt:brush style="NULL" color="#000000" printColorNumber="1" id="0"></pt:brush>\n';
    xml += `<pt:expanded objectName="Barcode${id + 1}" ID="${id}" lock="0" `;
    xml += 'templateMergeTarget="LABELLIST" templateMergeType="NONE" templateMergeID="0" ';
    xml += 'allowOutOfBoundsTransfer="false" linkStatus="NONE" linkID="0"></pt:expanded>\n';
    xml += '</pt:objectStyle>\n';
    
    xml += '<barcode:barcodeInfo>\n';
    xml += '<barcode:barcodeType>QRCode</barcode:barcodeType>\n';
    xml += `<barcode:barcodeData>${this.escapeXml(data)}</barcode:barcodeData>\n`;
    xml += '<barcode:barcodeOption>\n';
    xml += '<barcode:qrCode errorCorrection="M" dataType="0" model="2"></barcode:qrCode>\n';
    xml += '</barcode:barcodeOption>\n';
    xml += '</barcode:barcodeInfo>\n';
    
    xml += '</barcode:barcode>\n';
    
    return xml;
  }

  /**
   * Generiert ein Logo-BMP für die .lbx-Datei
   */
  private async generateLogoBmp(size: number = 192): Promise<Uint8Array> {
    try {
      // Lade das echte Firmenlogo
      const logoUrl = 'https://inv.dev.terhorst.io/uploads/logo-1759361666281-795237868.png';
      const logoBmp = await this.loadImageAsBmp(logoUrl, size, size);
      if (logoBmp) {
        console.log(`Firmenlogo erfolgreich geladen und zu BMP konvertiert (${size}x${size}px)`);
        return logoBmp;
      }
    } catch (error) {
      console.log('Logo nicht gefunden, verwende Fallback:', error);
    }

    // Fallback: Erstelle ein einfaches Logo-BMP (dynamische Größe, 24-bit)
    const width = size;
    const height = size;
    const bpp = 24; // bits per pixel (24-bit für bessere Kompatibilität)
    const bytesPerPixel = 3; // 24 bits = 3 bytes
    const rowSize = Math.ceil((width * bpp) / 32) * 4; // 4-byte alignment
    const imageSize = rowSize * height;
    const fileSize = 54 + imageSize; // BMP header + image data

    const bmp = new Uint8Array(fileSize);
    let offset = 0;

    // BMP Header
    bmp[offset++] = 0x42; // 'B'
    bmp[offset++] = 0x4D; // 'M'
    this.writeUint32LE(bmp, offset, fileSize); offset += 4; // File size
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Reserved
    this.writeUint32LE(bmp, offset, 54); offset += 4; // Data offset

    // DIB Header
    this.writeUint32LE(bmp, offset, 40); offset += 4; // Header size
    this.writeUint32LE(bmp, offset, width); offset += 4; // Width
    this.writeUint32LE(bmp, offset, height); offset += 4; // Height
    this.writeUint16LE(bmp, offset, 1); offset += 2; // Planes
    this.writeUint16LE(bmp, offset, bpp); offset += 2; // Bits per pixel
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Compression
    this.writeUint32LE(bmp, offset, imageSize); offset += 4; // Image size
    this.writeUint32LE(bmp, offset, 3780); offset += 4; // X pixels per meter
    this.writeUint32LE(bmp, offset, 3780); offset += 4; // Y pixels per meter
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Colors in color table
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Important color count

    // Image data (BMP ist bottom-up, also von unten nach oben)
    for (let y = height - 1; y >= 0; y--) {
      for (let x = 0; x < width; x++) {
        const pixelOffset = offset + (y * rowSize) + (x * bytesPerPixel);
        
        // Einfaches Logo-Design: weißer Hintergrund mit schwarzem "W"
        let r = 255, g = 255, b = 255; // Weiß
        
        // Schwarzer Rand
        if (x < 10 || x >= width - 10 || y < 10 || y >= height - 10) {
          r = g = b = 0; // Schwarz
        }
        // Einfaches "W" in der Mitte
        else if (this.isInWShape(x, y, width, height)) {
          r = g = b = 0; // Schwarz
        }
        
        // BMP verwendet BGR-Reihenfolge (nicht RGB!)
        bmp[pixelOffset] = b;     // Blue
        bmp[pixelOffset + 1] = g; // Green
        bmp[pixelOffset + 2] = r; // Red
      }
    }

    return bmp;
  }

  /**
   * Generiert einen QR-Code-BMP für die .lbx-Datei
   */
  private async generateQrCodeBmp(qrData: string, size: number = 300): Promise<Uint8Array> {
    try {
      // Generiere QR-Code als Canvas
      const QRCode = (await import('qrcode')).default;
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, qrData, {
        width: size,
        margin: 0,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });

      // Konvertiere Canvas zu BMP
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context nicht verfügbar');

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      return this.imageDataToBmp(imageData, canvas.width, canvas.height);
    } catch (error) {
      console.error('Fehler beim Generieren des QR-Code-BMP:', error);
      // Fallback: Einfaches schwarzes Quadrat
      return this.generateFallbackQrBmp(size);
    }
  }

  /**
   * Konvertiert ImageData zu BMP-Format
   */
  private imageDataToBmp(imageData: ImageData, width: number, height: number): Uint8Array {
    const bpp = 24; // bits per pixel (24-bit für bessere Kompatibilität)
    const bytesPerPixel = 3; // 24 bits = 3 bytes
    const rowSize = Math.ceil((width * bpp) / 32) * 4; // 4-byte alignment
    const imageSize = rowSize * height;
    const fileSize = 54 + imageSize; // BMP header + image data

    const bmp = new Uint8Array(fileSize);
    let offset = 0;

    // BMP Header
    bmp[offset++] = 0x42; // 'B'
    bmp[offset++] = 0x4D; // 'M'
    this.writeUint32LE(bmp, offset, fileSize); offset += 4; // File size
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Reserved
    this.writeUint32LE(bmp, offset, 54); offset += 4; // Data offset

    // DIB Header
    this.writeUint32LE(bmp, offset, 40); offset += 4; // Header size
    this.writeUint32LE(bmp, offset, width); offset += 4; // Width
    this.writeUint32LE(bmp, offset, height); offset += 4; // Height
    this.writeUint16LE(bmp, offset, 1); offset += 2; // Planes
    this.writeUint16LE(bmp, offset, bpp); offset += 2; // Bits per pixel
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Compression
    this.writeUint32LE(bmp, offset, imageSize); offset += 4; // Image size
    this.writeUint32LE(bmp, offset, 3780); offset += 4; // X pixels per meter
    this.writeUint32LE(bmp, offset, 3780); offset += 4; // Y pixels per meter
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Colors in color table
    this.writeUint32LE(bmp, offset, 0); offset += 4; // Important color count

    // Image data (BMP ist bottom-up, ImageData ist top-down)
    for (let y = height - 1; y >= 0; y--) {
      for (let x = 0; x < width; x++) {
        const pixelOffset = offset + (y * rowSize) + (x * bytesPerPixel);
        const dataOffset = ((height - 1 - y) * width + x) * 4;
        
        // BMP verwendet BGR-Reihenfolge (nicht RGB!)
        bmp[pixelOffset] = imageData.data[dataOffset + 2];     // Blue
        bmp[pixelOffset + 1] = imageData.data[dataOffset + 1]; // Green
        bmp[pixelOffset + 2] = imageData.data[dataOffset];     // Red
        // Alpha-Kanal wird ignoriert bei 24-bit BMP
      }
    }

    return bmp;
  }

  /**
   * Generiert einen Fallback-QR-Code-BMP
   */
  private generateFallbackQrBmp(size: number = 300): Uint8Array {
    const width = size;
    const height = size;
    const bpp = 24; // 24-bit für bessere Kompatibilität
    const bytesPerPixel = 3; // 24 bits = 3 bytes
    const rowSize = Math.ceil((width * bpp) / 32) * 4; // 4-byte alignment
    const imageSize = rowSize * height;
    const fileSize = 54 + imageSize;

    const bmp = new Uint8Array(fileSize);
    let offset = 0;

    // BMP Header
    bmp[offset++] = 0x42; bmp[offset++] = 0x4D;
    this.writeUint32LE(bmp, offset, fileSize); offset += 4;
    this.writeUint32LE(bmp, offset, 0); offset += 4;
    this.writeUint32LE(bmp, offset, 54); offset += 4;
    this.writeUint32LE(bmp, offset, 40); offset += 4;
    this.writeUint32LE(bmp, offset, width); offset += 4;
    this.writeUint32LE(bmp, offset, height); offset += 4;
    this.writeUint16LE(bmp, offset, 1); offset += 2;
    this.writeUint16LE(bmp, offset, bpp); offset += 2;
    this.writeUint32LE(bmp, offset, 0); offset += 4;
    this.writeUint32LE(bmp, offset, imageSize); offset += 4;
    this.writeUint32LE(bmp, offset, 3780); offset += 4;
    this.writeUint32LE(bmp, offset, 3780); offset += 4;
    this.writeUint32LE(bmp, offset, 0); offset += 4;
    this.writeUint32LE(bmp, offset, 0); offset += 4;

    // Einfaches schwarzes Quadrat
    for (let y = height - 1; y >= 0; y--) {
      for (let x = 0; x < width; x++) {
        const pixelOffset = offset + (y * rowSize) + (x * bytesPerPixel);
        bmp[pixelOffset] = 0;     // Blue
        bmp[pixelOffset + 1] = 0; // Green
        bmp[pixelOffset + 2] = 0; // Red
        // Alpha-Kanal wird ignoriert bei 24-bit BMP
      }
    }

    return bmp;
  }

  /**
   * Hilfsfunktionen für BMP-Generierung
   */
  private writeUint32LE(buffer: Uint8Array, offset: number, value: number): void {
    buffer[offset] = value & 0xFF;
    buffer[offset + 1] = (value >> 8) & 0xFF;
    buffer[offset + 2] = (value >> 16) & 0xFF;
    buffer[offset + 3] = (value >> 24) & 0xFF;
  }

  private writeUint16LE(buffer: Uint8Array, offset: number, value: number): void {
    buffer[offset] = value & 0xFF;
    buffer[offset + 1] = (value >> 8) & 0xFF;
  }

  /**
   * Lädt ein Bild und konvertiert es zu BMP
   */
  private async loadImageAsBmp(imageUrl: string, targetWidth: number, targetHeight: number): Promise<Uint8Array | null> {
    try {
      const img = new Image();
      
      // Für externe URLs CORS aktivieren
      if (imageUrl.startsWith('http')) {
        img.crossOrigin = 'anonymous';
      }
      
      return new Promise((resolve) => {
        img.onload = () => {
          try {
            console.log(`Bild geladen: ${img.width}x${img.height}px, Ziel: ${targetWidth}x${targetHeight}px`);
            
            // Erstelle Canvas
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              console.error('Canvas-Kontext nicht verfügbar');
              resolve(null);
              return;
            }

            // Setze Canvas-Dimensionen auf Zielgröße
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            // Aktiviere Bildglättung für bessere Qualität
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Zeichne Bild auf Canvas (skaliert auf exakte Zielgröße)
            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

            // Konvertiere zu ImageData
            const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
            
            // Konvertiere zu BMP
            const bmp = this.imageDataToBmp(imageData, targetWidth, targetHeight);
            console.log(`BMP generiert: ${bmp.length} Bytes für ${targetWidth}x${targetHeight}px`);
            resolve(bmp);
          } catch (error) {
            console.error('Fehler beim Konvertieren des Bildes:', error);
            resolve(null);
          }
        };

        img.onerror = (error) => {
          console.error('Fehler beim Laden des Bildes:', imageUrl, error);
          resolve(null);
        };

        // Timeout nach 10 Sekunden
        setTimeout(() => {
          console.error('Timeout beim Laden des Bildes:', imageUrl);
          resolve(null);
        }, 10000);

        img.src = imageUrl;
      });
    } catch (error) {
      console.error('Fehler beim Laden des Bildes:', error);
      return null;
    }
  }

  /**
   * Prüft ob ein Pixel Teil des "W"-Shapes ist
   */
  private isInWShape(x: number, y: number, width: number, height: number): boolean {
    const centerX = width / 2;
    const centerY = height / 2;
    const wWidth = width * 0.6;
    const wHeight = height * 0.6;
    
    const relX = (x - centerX) / (wWidth / 2);
    const relY = (y - centerY) / (wHeight / 2);
    
    // Einfaches "W" Pattern
    return Math.abs(relY) < 0.8 && (
      (relX > -1 && relX < -0.6) || // Linker Strich
      (relX > -0.3 && relX < 0.1) || // Mittlerer Strich
      (relX > 0.4 && relX < 0.8) || // Rechter Strich
      (relX > -0.6 && relX < 0.4 && relY > 0.2) // Verbindungslinie
    );
  }

  /**
   * Erstellt die prop.xml für Brother P-Touch Editor (exakt wie im Original)
   */
  private createPropXml(): string {
    const now = new Date().toISOString();
    
    let xml = '<?xml version="1.0" encoding="UTF-8"?>';
    xml += '<meta:properties xmlns:meta="http://schemas.brother.info/ptouch/2007/lbx/meta" ';
    xml += 'xmlns:dc="http://purl.org/dc/elements/1.1/" ';
    xml += 'xmlns:dcterms="http://purl.org/dc/terms/">';
    xml += '<meta:appName>P-touch Editor 6.8.1.0 Windows</meta:appName>';
    xml += '<dc:title></dc:title>';
    xml += '<dc:subject></dc:subject>';
    xml += '<dc:creator></dc:creator>';
    xml += '<meta:keyword></meta:keyword>';
    xml += '<dc:description></dc:description>';
    xml += '<meta:template></meta:template>';
    xml += `<dcterms:created>${now}</dcterms:created>`;
    xml += `<dcterms:modified>${now}</dcterms:modified>`;
    xml += '<meta:lastPrinted></meta:lastPrinted>';
    xml += '<meta:modifiedBy></meta:modifiedBy>';
    xml += '<meta:revision>3</meta:revision>';
    xml += '<meta:editTime>0</meta:editTime>';
    xml += '<meta:numPages>1</meta:numPages>';
    xml += '<meta:numWords>0</meta:numWords>';
    xml += '<meta:numChars>0</meta:numChars>';
    xml += '<meta:security>0</meta:security>';
    xml += '<meta:transferScript></meta:transferScript>';
    xml += '</meta:properties>';
    
    return xml;
  }

  /**
   * Escaped XML-Zeichen
   */
  private escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }


  /**
   * Generiert eine CSV-Datei für Brother P-Touch Designer
   */
  async generateCSVLabelFile(labelData: LabelData): Promise<LabelFileResult> {
    try {
      const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;

      // CSV Header
      let csvContent = 'Template,Width,Height,Text1,Text2,Text3,Text4,Text5,QRCode\n';

      // CSV Data
      const texts = [];
      
      if (options.includeLogo && template.supportsLogo) {
        texts.push('Westfalen AG');
      }
      
      texts.push(itemName);
      
      if (sku) {
        texts.push(`Mat. Nr: ${sku}`);
      }
      
      if (serialNumber) {
        texts.push(`SN: ${serialNumber}`);
      }
      
      if (customText) {
        texts.push(customText);
      }

      // Fülle auf 5 Text-Felder auf
      while (texts.length < 5) {
        texts.push('');
      }

      const qrCodeValue = (options.includeQR && template.supportsQR && qrCode) ? qrCode : '';

      csvContent += `${template.name},${template.width},${template.height},"${texts[0]}","${texts[1]}","${texts[2]}","${texts[3]}","${texts[4]}","${qrCodeValue}"\n`;

      const fileName = `labels-${Date.now()}.csv`;

      return {
        success: true,
        message: 'CSV-Datei erfolgreich generiert',
        fileName,
        fileContent: csvContent
      };
    } catch (error) {
      console.error('Fehler beim Generieren der CSV-Datei:', error);
      return {
        success: false,
        message: `Fehler beim Generieren der CSV-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      };
    }
  }

  /**
   * Generiert eine SVG-Datei für die Vorschau
   */
  async generateSVGLabelFile(labelData: LabelData): Promise<LabelFileResult> {
    try {
      const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;

      // QR-Code generieren
      let qrCodeSvg = '';
      if (options.includeQR && template.supportsQR && qrCode) {
        qrCodeSvg = await QRCode.toString(qrCode, {
          type: 'svg',
          width: Math.min(template.width * 2, 100),
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      }

      // SVG erstellen
      const mmToPx = 3.7795275591;
      const widthPx = template.width * mmToPx;
      const heightPx = template.height * mmToPx;

      let svgContent = `<svg width="${widthPx}" height="${heightPx}" xmlns="http://www.w3.org/2000/svg">\n`;
      svgContent += `  <rect width="100%" height="100%" fill="white" stroke="#ccc" stroke-width="1"/>\n`;

      let yOffset = 10;

      // Logo
      if (options.includeLogo && template.supportsLogo) {
        svgContent += `  <text x="5" y="${yOffset}" font-family="Arial, sans-serif" font-size="8" fill="#666">Westfalen AG</text>\n`;
        yOffset += 10;
      }

      // Item Name
      svgContent += `  <text x="5" y="${yOffset}" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#000">${itemName}</text>\n`;
      yOffset += 15;

      // SKU
      if (sku) {
        svgContent += `  <text x="5" y="${yOffset}" font-family="Arial, sans-serif" font-size="8" fill="#333">Mat. Nr: ${sku}</text>\n`;
        yOffset += 10;
      }

      // Serial Number
      if (serialNumber) {
        svgContent += `  <text x="5" y="${yOffset}" font-family="Arial, sans-serif" font-size="8" fill="#333">SN: ${serialNumber}</text>\n`;
        yOffset += 10;
      }

      // QR Code
      if (qrCodeSvg) {
        const qrSize = Math.min(template.width * 1.5, heightPx - yOffset - 5);
        const qrX = (widthPx - qrSize) / 2;
        
        // QR-Code SVG einbetten
        const qrCodeEmbedded = qrCodeSvg
          .replace('<svg', `<g x="${qrX}" y="${yOffset}" width="${qrSize}" height="${qrSize}"`)
          .replace(/<svg[^>]*>/, '')
          .replace('</svg>', '</g>');
        
        svgContent += `  ${qrCodeEmbedded}\n`;
        yOffset += qrSize + 5;
      }

      // Custom Text
      if (customText) {
        svgContent += `  <text x="5" y="${yOffset}" font-family="Arial, sans-serif" font-size="8" fill="#666">${customText}</text>\n`;
      }

      svgContent += `</svg>`;

      const fileName = `label-${itemName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.svg`;

      return {
        success: true,
        message: 'SVG-Datei erfolgreich generiert',
        fileName,
        fileContent: svgContent
      };
    } catch (error) {
      console.error('Fehler beim Generieren der SVG-Datei:', error);
      return {
        success: false,
        message: `Fehler beim Generieren der SVG-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      };
    }
  }

  /**
   * Generiert eine PDF-Datei für den Druck
   */
  async generatePDFLabelFile(labelData: LabelData): Promise<LabelFileResult> {
    try {
      const { itemName, sku, serialNumber, qrCode, customText, template, options } = labelData;

      // QR-Code generieren
      let qrCodeDataUrl = '';
      if (options.includeQR && template.supportsQR && qrCode) {
        qrCodeDataUrl = await QRCode.toDataURL(qrCode, {
          width: 128,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
      }

      // PDF mit jsPDF generieren
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [template.width, template.height]
      });

      let yPosition = 2;

      // Logo
      if (options.includeLogo && template.supportsLogo) {
        doc.setFontSize(6);
        doc.text('Westfalen AG', 2, yPosition);
        yPosition += 3;
      }

      // Item Name
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(itemName, 2, yPosition);
      yPosition += 4;

      // SKU
      if (sku) {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.text(`Mat. Nr: ${sku}`, 2, yPosition);
        yPosition += 3;
      }

      // Serial Number
      if (serialNumber) {
        doc.setFontSize(6);
        doc.text(`SN: ${serialNumber}`, 2, yPosition);
        yPosition += 3;
      }

      // QR Code
      if (qrCodeDataUrl) {
        const qrSize = Math.min(20, template.width - 4);
        const qrX = (template.width - qrSize) / 2;
        doc.addImage(qrCodeDataUrl, 'PNG', qrX, yPosition, qrSize, qrSize);
        yPosition += qrSize + 2;
      }

      // Custom Text
      if (customText) {
        doc.setFontSize(6);
        doc.text(customText, 2, yPosition);
      }

      const fileName = `label-${itemName.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}.pdf`;
      const pdfBlob = doc.output('blob');

      return {
        success: true,
        message: 'PDF-Datei erfolgreich generiert',
        fileName,
        fileContent: await this.blobToBase64(pdfBlob)
      };
    } catch (error) {
      console.error('Fehler beim Generieren der PDF-Datei:', error);
      return {
        success: false,
        message: `Fehler beim Generieren der PDF-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`
      };
    }
  }

  /**
   * Konvertiert Blob zu Base64
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Lädt eine Datei herunter
   */
  downloadFile(fileName: string, fileContent: string, mimeType: string = 'text/plain'): void {
    const blob = new Blob([fileContent], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Lädt eine Base64-Datei herunter
   */
  downloadBase64File(fileName: string, base64Content: string): void {
    const a = document.createElement('a');
    a.href = base64Content;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export const labelFileService = new LabelFileService();
